/**
 * admin-settlement-actions.ts - 강사별 정산 액션 서비스
 *
 * Cloud Function 호출 래퍼 및 Firestore 정산 데이터 조회
 */

import { httpsCallable } from "firebase/functions";
import { doc, getDoc, type Timestamp } from "firebase/firestore";
import { getFirebaseFirestore, getFirebaseFunctions } from "@/config/firebase";
import type {
  InstructorSettlement,
  SettlementLineItem,
  SettlementStatus,
} from "@vlab-corporation/vlab-pilates-service-interface/settlement";

/**
 * 정산 완료된 InstructorSettlement 확장 타입
 * Firestore에 저장된 settledAt/settledBy 및 월별 정산 status 포함
 */
export interface SettledInstructorSettlement extends InstructorSettlement {
  settledAt?: Timestamp;
  settledBy?: string;
  /** 월별 정산 상태 (calculating | confirmed | paid) */
  status?: SettlementStatus;
}

/** settleInstructor CF 응답 타입 */
interface SettleInstructorResponse {
  success: true;
  instructorSettlement: {
    instructorId: string;
    completedClasses: number;
    totalAmount: number;
  };
}

/**
 * 특정 강사의 월 정산을 실행한다.
 * settleInstructor Cloud Function을 호출하여 계산 + 저장 + 완료 처리를 수행한다.
 *
 * @param centerId - 센터 ID
 * @param instructorId - 강사 ID
 * @param yearMonth - 정산 대상 연월 ("YYYY-MM" 형식)
 * @returns 정산 결과 (instructorId, completedClasses, totalAmount)
 */
export async function settleInstructor(
  centerId: number,
  instructorId: string,
  yearMonth: string,
): Promise<SettleInstructorResponse> {
  const callable = httpsCallable<
    { centerId: number; instructorId: string; yearMonth: string },
    SettleInstructorResponse
  >(getFirebaseFunctions(), "settleInstructor");

  const result = await callable({ centerId, instructorId, yearMonth });
  return result.data;
}

/** cancelInstructorSettlement CF 응답 타입 */
interface CancelInstructorSettlementResponse {
  success: true;
  remainingInstructors: number;
}

/**
 * 특정 강사의 정산을 취소한다.
 * cancelInstructorSettlement Cloud Function을 호출하여 해당 강사의 정산을 제거하고 재정산을 가능하게 한다.
 *
 * @param centerId - 센터 ID
 * @param instructorId - 강사 ID
 * @param yearMonth - 정산 대상 연월 ("YYYY-MM" 형식)
 * @param reason - 취소 사유
 * @returns 취소 결과 (남은 강사 수)
 */
export async function cancelInstructorSettlement(
  centerId: number,
  instructorId: string,
  yearMonth: string,
  reason: string,
): Promise<CancelInstructorSettlementResponse> {
  const callable = httpsCallable<
    { centerId: number; instructorId: string; yearMonth: string; reason: string },
    CancelInstructorSettlementResponse
  >(getFirebaseFunctions(), "cancelInstructorSettlement");

  const result = await callable({ centerId, instructorId, yearMonth, reason });
  return result.data;
}

/** previewInstructorSettlement CF 응답 타입 */
interface PreviewInstructorSettlementResponse {
  instructorSettlement: {
    instructorId: string;
    completedClasses: number;
    totalAmount: number;
    lineItems: SettlementLineItem[];
  };
}

/**
 * 특정 강사의 월 정산을 미리 계산한다 (dry-run).
 * Firestore에 저장하지 않고 예상 금액과 라인 아이템만 반환한다.
 *
 * @param centerId - 센터 ID
 * @param instructorId - 강사 ID
 * @param yearMonth - 정산 대상 연월 ("YYYY-MM" 형식)
 * @returns 예상 정산 결과 (lineItems 포함)
 */
export async function previewInstructorSettlement(
  centerId: number,
  instructorId: string,
  yearMonth: string,
): Promise<PreviewInstructorSettlementResponse> {
  const callable = httpsCallable<
    { centerId: number; instructorId: string; yearMonth: string },
    PreviewInstructorSettlementResponse
  >(getFirebaseFunctions(), "previewInstructorSettlement");

  const result = await callable({ centerId, instructorId, yearMonth });
  return result.data;
}

/** adjustSettlementLineItem Cloud Function 호출 파라미터 */
export interface AdjustLineItemParams {
  centerId: number;
  instructorId: string;
  yearMonth: string;
  classId: string;
  /**
   * 조정 후 금액 (선택, 0 이상의 정수).
   * unitPrice 제공 시 amount는 unitPrice로 설정되므로 이 값은 무시된다.
   */
  adjustedAmount?: number;
  /** 수정 사유 (선택, 미전달 시 CF에서 기본값 사용) */
  reason?: string;
  /** 위탁대금 직접 수정 (선택, 0 이상의 정수) */
  unitPrice?: number;
  /** 비고/메모 (선택) */
  notes?: string;
}

/**
 * 정산 라인 아이템을 수동으로 조정한다.
 * adjustSettlementLineItem Cloud Function을 호출하여 특정 수업의 정산 금액이나 위탁대금을 수정한다.
 *
 * @param params - 조정 파라미터 (centerId, instructorId, yearMonth, classId, 및 선택적 조정 필드)
 */
export async function adjustLineItem(params: AdjustLineItemParams): Promise<void> {
  const callable = httpsCallable<AdjustLineItemParams, undefined>(
    getFirebaseFunctions(),
    "adjustSettlementLineItem",
  );

  await callable(params);
}

/**
 * 특정 강사의 정산 데이터를 Firestore에서 조회한다.
 *
 * settlements/{yearMonth} 문서의 instructorSettlements 배열에서
 * 해당 강사의 엔트리를 찾아 반환한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param yearMonth - 조회 대상 연월 ("YYYY-MM" 형식)
 * @param instructorId - 강사 ID
 * @returns 정산 데이터 또는 null (정산 없는 경우)
 */
export async function getInstructorSettlement(
  ownerUid: string,
  centerId: number,
  yearMonth: string,
  instructorId: string,
): Promise<SettledInstructorSettlement | null> {
  const ref = doc(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "settlements",
    yearMonth,
  );
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;

  const data = snap.data() as {
    status?: SettlementStatus;
    instructorSettlements?: SettledInstructorSettlement[];
  };
  const entry = data.instructorSettlements?.find((s) => s.instructorId === instructorId);
  if (!entry) return null;
  // 월별 정산의 status를 강사별 엔트리에 주입하여 반환
  return { ...entry, status: data.status };
}
